<script>
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import {
    ButtonGroup,
    Checkbox,
    Input,
    InputAddon,
    Label,
    Select,
  } from 'flowbite-svelte';
  import COUNTRY_CODE_DATA from '../../../../lib/country_codes.json';
  import {
    MUTATION_KEY_USER_PROFILE,
    QUERY_KEY_USER_PROFILE,
    QUERY_KEY_USER_DATA,
    TOAST_ERROR,
    TOAST_SUCCESS,
  } from '../../../constants';
  import {
    queryUserProfileQuery,
    updateUserProfile,
  } from '../../../data/userData';
  import { addToast } from '../../../store';
  import { _ } from 'svelte-i18n';

  export let onSuccess = () => {};
  export let loading = false;

  const onError = (message) => {
    addToast({
      message,
      type: TOAST_ERROR,
      timeout: 3000,
    });
  };

  const handleOnSuccess = (message) => {
    addToast({
      message,
      type: TOAST_SUCCESS,
      timeout: 3000,
    });

    onSuccess();
  };

  const queryClient = useQueryClient();
  const userProfile = queryUserProfileQuery();
  const mutation = createMutation({
    mutationKey: MUTATION_KEY_USER_PROFILE,
    mutationFn: updateUserProfile,
    onMutate: () => {
      loading = true;
    },
    onSettled: () => {
      loading = false;
      queryClient.invalidateQueries([
        QUERY_KEY_USER_DATA,
        QUERY_KEY_USER_PROFILE,
      ]);
    },
    onError: (err) => {
      onError(err.message);
    },
    onSuccess: () => {
      handleOnSuccess(
        $_('components.managedProfileForm.toast.success', {
          default: 'Profile updated successfully',
        }),
      );
    },
  });

  // Form fields
  let { firstName, lastName, instagram, country, tiktok, emailPreference } =
    $userProfile?.data?.object || {};

  export const submitForm = async (event) => {
    event.preventDefault();

    $mutation.mutate({
      firstName,
      lastName,
      country: country || undefined,
      instagram,
      tiktok,
      emailPreference,
    });
  };

  $: {
    const refreshedData = $userProfile?.data?.object;
    firstName =
      refreshedData?.firstName && !firstName
        ? refreshedData.firstName
        : firstName;
    lastName =
      refreshedData?.lastName && !lastName ? refreshedData.lastName : lastName;
    country =
      refreshedData?.country && !country ? refreshedData.country : country;
    instagram =
      refreshedData?.instagram && !instagram
        ? refreshedData.instagram
        : instagram;
    tiktok = refreshedData?.tiktok && !tiktok ? refreshedData.tiktok : tiktok;
    emailPreference =
      refreshedData?.emailPreference && !emailPreference
        ? refreshedData.emailPreference
        : emailPreference;
  }

  const onCountryChange = () => {
    emailPreference = country === 'US';
  };
</script>

{#if $userProfile.isError}
  {`${$_('components.managedProfileForm.errors.occured', { default: 'An error has occurred' })}: ${$userProfile.error.message}`}
{/if}
{#if $userProfile.isSuccess}
  <div class="grid w-full max-w-6xl grid-cols-2 gap-4">
    <div class="col-span-2 md:col-span-1">
      <Label for="fullName_profileModal">
        <div class="mb-2">
          {$_('components.managedProfileForm.fields.firstName', {
            default: 'First Name',
          })}
        </div>
        <Input
          id="fullName_profileModal"
          maxlength="256"
          bind:value={firstName}
        />
      </Label>
    </div>
    <div class="col-span-2 md:col-span-1">
      <Label
        ><div class="mb-2">
          {$_('components.managedProfileForm.fields.lastName', {
            default: 'Last Name',
          })}
        </div>
        <Input maxlength="256" bind:value={lastName} />
      </Label>
    </div>
    <div class="col-span-2">
      <Label>
        <div class="mb-2">
          {$_('components.managedProfileForm.fields.country', {
            default: 'Country',
          })}
        </div>
        <Select
          required
          on:change={onCountryChange}
          items={COUNTRY_CODE_DATA.map((c) => ({
            name: c.name,
            value: c.code,
          }))}
          bind:value={country}
        />
      </Label>
    </div>
    <div class="col-span-2 md:col-span-1">
      <Label>
        <div class="mb-2">
          {$_('components.managedProfileForm.fields.instagram', {
            default: 'Instagram Handle',
          })}
        </div>
        <ButtonGroup class="w-full">
          <InputAddon>@</InputAddon>
          <Input maxlength="256" bind:value={instagram} />
        </ButtonGroup>
      </Label>
    </div>
    <div class="col-span-2 md:col-span-1">
      <Label>
        <div class="mb-2">
          {$_('components.managedProfileForm.fields.tiktok', {
            default: 'Tiktok Handle',
          })}
        </div>
        <ButtonGroup class="w-full">
          <InputAddon>@</InputAddon>
          <Input maxlength="256" bind:value={tiktok} />
        </ButtonGroup>
      </Label>
    </div>
    <div class="col-span-2">
      <Checkbox bind:checked={emailPreference}>
        {$_('components.managedProfileForm.fields.getUpdates', {
          default: 'Get Updates from Button',
        })}
      </Checkbox>
    </div>
  </div>
{/if}
